import { Box, Slider, Typography } from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { optionVariants } from "./quizMotion";

/**
 * Отрисовка одного вопроса теста.
 * Типы: single | multiple | scale.
 * answer: { option_ids: number[], numeric_value: number|null }
 */
export default function QuizQuestion({ question, answer, onChange }) {
  const type = question.question_type;

  const isSelected = (optId) => (answer?.option_ids || []).includes(optId);

  const toggleOption = (optId) => {
    if (type === "single") {
      onChange({ option_ids: [optId], numeric_value: null });
    } else if (type === "multiple") {
      const current = new Set(answer?.option_ids || []);
      if (current.has(optId)) current.delete(optId);
      else current.add(optId);
      onChange({ option_ids: Array.from(current), numeric_value: null });
    }
  };

  const min = question.scale_min ?? 0;
  const max = question.scale_max ?? 10;
  const step = question.scale_step ?? 1;
  const scaleValue = answer?.numeric_value ?? Math.round((min + max) / 2);

  return (
    <Box>
      {question.image && (
        <Box
          component={motion.img}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          src={question.image}
          alt=""
          sx={{
            width: "100%",
            maxHeight: 200,
            objectFit: "cover",
            borderRadius: "lg",
            mb: 2,
          }}
        />
      )}

      <Typography level="h3" sx={{ mb: question.description ? 0.5 : 2, lineHeight: 1.25 }}>
        {question.text}
      </Typography>
      {question.description && (
        <Typography level="body-sm" sx={{ color: "text.tertiary", mb: 2 }}>
          {question.description}
        </Typography>
      )}

      {type === "scale" ? (
        <Box sx={{ px: 1, pt: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <AnimatePresence mode="popLayout">
              <Typography
                key={scaleValue}
                component={motion.div}
                initial={{ scale: 0.6, opacity: 0, y: 8 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.6, opacity: 0, y: -8 }}
                level="h1"
                sx={{ color: "primary.500", fontWeight: 800 }}
              >
                {scaleValue}
              </Typography>
            </AnimatePresence>
          </Box>
          <Slider
            value={scaleValue}
            min={min}
            max={max}
            step={step}
            marks
            valueLabelDisplay="off"
            onChange={(_, v) => onChange({ option_ids: [], numeric_value: v })}
            sx={{ "--Slider-trackSize": "8px", "--Slider-thumbSize": "22px" }}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>{min}</Typography>
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>{max}</Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
          {(question.options || []).map((opt, i) => {
            const selected = isSelected(opt.id);
            return (
              <Box
                key={opt.id}
                component={motion.button}
                type="button"
                custom={i}
                variants={optionVariants}
                initial="hidden"
                animate="visible"
                whileTap={{ scale: 0.98 }}
                onClick={() => toggleOption(opt.id)}
                sx={{
                  textAlign: "left",
                  cursor: "pointer",
                  font: "inherit",
                  color: "text.primary",
                  border: "2px solid",
                  borderColor: selected ? "primary.500" : "background.level2",
                  backgroundColor: selected ? "primary.100" : "background.surface",
                  borderRadius: "lg",
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  transition: "border-color .2s, background-color .2s",
                }}
              >
                {opt.image && (
                  <Box
                    component="img"
                    src={opt.image}
                    alt=""
                    sx={{ width: 48, height: 48, borderRadius: "md", objectFit: "cover", flexShrink: 0 }}
                  />
                )}
                <Typography sx={{ flex: 1, fontWeight: selected ? 700 : 500 }}>{opt.text}</Typography>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    flexShrink: 0,
                    border: "2px solid",
                    borderColor: selected ? "primary.500" : "neutral.300",
                    backgroundColor: selected ? "primary.500" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AnimatePresence>
                    {selected && (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        style={{ display: "flex" }}
                      >
                        <CheckRoundedIcon sx={{ fontSize: 16, color: "#fff" }} />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}